// Shared value formatting for GA4 parameters, so every event applies the spec's
// global rules the same way.

// Spec §2: parameter values are capped at 100 characters.
export const MAX_VALUE_LENGTH = 100;

// Spec §2: never push an empty value — drop the parameter instead.
export const clean = (value) => {
  if (value === undefined || value === null) return undefined;
  const text = String(value).trim();
  return text ? text.slice(0, MAX_VALUE_LENGTH) : undefined;
};

// Catalog fields that are single-valued in the spec (`name`, `sku`) come back
// from some endpoints as arrays.
export const first = (value) => (Array.isArray(value) ? value.at(0) : value);

export const round2 = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? +number.toFixed(2) : undefined;
};

// Used to derive stable, language-independent list ids from English labels.
export const slugify = (value) =>
  clean(
    String(value ?? '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
  );

// "skin-care" -> "Skin Care". Spec §2 requires English values, and a slug is
// the only language-independent label a category carries, so list names are
// derived from it rather than from the locale's category title.
export const titleFromSlug = (value) =>
  clean(
    String(value ?? '')
      .split(/[^a-zA-Z0-9]+/)
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  );

// The absolute URL a link actually navigates to — `item_link`,
// `promotion_destination`. Routing is `localePrefix: 'as-needed'` with `ar` as
// the default locale, so only English URLs carry a prefix.
export const getAbsoluteUrl = (path, locale) => {
  const target = clean(path);
  if (!target) return undefined;

  // Already absolute (external campaign links, CDN image URLs) — leave alone.
  if (/^https?:\/\//i.test(target)) return target;

  const origin =
    typeof window === 'object'
      ? window.location.origin
      : process.env.NEXT_PUBLIC_WEBSITE_URL || '';

  const prefix = String(locale).toLowerCase() === 'en' ? '/en' : '';

  return clean(`${origin}${prefix}/${target.replace(/^\/+/, '')}`);
};
